// E8PowerQuotientTruthClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 4) - dzialania na potegach (P/F).
// Zd.1: 9^p : 3^q = 3^(2p-q).  Zd.2: 3^r jest 3 razy wieksza od 3^a*3^b = 3^(a+b)  <=>  r = a+b+1.

import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import TruthInput from "../../../../components/inputs/TruthInput";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData({ p, q, claim1, a, b, claim2 }) {
  const real1 = 2 * p - q; // 9^p = 3^(2p), dzielenie => 2p - q
  const real2 = a + b + 1; // 3 razy wieksza => wykladnik +1
  return {
    p,
    q,
    claim1,
    a,
    b,
    claim2,
    correctPF: [claim1 === real1 ? "P" : "F", claim2 === real2 ? "P" : "F"],
  };
}

function createOriginalTask() {
  return {
    type: "E8PowerQuotientTruthClosedTask",
    // 9^4 : 3^2 = 3^6 (claim 3^2 => F);  3^8 vs 3*(3^5*3^2)=3^8 (claim r=8 => P)
    data: buildData({ p: 4, q: 2, claim1: 2, a: 5, b: 2, claim2: 8 }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 4 },
    tags: ["e8", "potegi", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const p = pick([3, 4, 5]);
  const q = pick([2, 4]);
  const real1 = 2 * p - q;
  const s1True = Math.random() < 0.5;
  const claim1 = s1True ? real1 : real1 + pick([-2, 2, 1, -1]).valueOf();

  const a = pick([3, 4, 5]);
  const b = pick([2, 3]);
  const real2 = a + b + 1;
  const s2True = Math.random() < 0.5;
  const claim2 = s2True ? real2 : real2 + pick([1, -1, 2]);

  return {
    type: "E8PowerQuotientTruthClosedTask",
    data: buildData({ p, q, claim1: Math.max(1, claim1), a, b, claim2: Math.max(1, claim2) }),
    source: { variant: "random", basedOn: "E8_2025_CZE_4" },
    tags: ["e8", "potegi", "prawda_falsz"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli zdanie
        jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>
      <p>
        1. Iloraz <InlineMath math={`9^{${data.p}}:3^{${data.q}}`} /> jest równy{" "}
        <InlineMath math={`3^{${data.claim1}}`} />.
      </p>
      <p>
        2. Liczba <InlineMath math={`3^{${data.claim2}}`} /> jest trzy razy większa od
        iloczynu <InlineMath math={`3^{${data.a}}\\cdot 3^{${data.b}}`} />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) => onChange?.({ ...value, [key]: nextValue });
  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput value={value[key] || null} disabled={disabled} onChange={(nextValue) => setValue(key, nextValue)} />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.s1 === data.correctPF[0] && userAnswer?.s2 === data.correctPF[1];
}

export function renderAnswer({ data }) {
  return <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\quad 2.\\ \\text{${userAnswer?.s2 || "-"}}`} />;
}

export function renderExplanation({ data }) {
  const { p, q, a, b } = data;
  const real1 = 2 * p - q;
  const real2 = a + b + 1;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Zdanie 1.</strong> Zapisujemy <InlineMath math={`9^{${p}}=3^{${2 * p}}`} /> i dzielimy potęgi:</p>
      <BlockMath math={`9^{${p}}:3^{${q}}=3^{${2 * p}}:3^{${q}}=3^{${2 * p}-${q}}=3^{${real1}}`} />
      <p>Zdanie 1. jest {data.correctPF[0] === "P" ? "prawdziwe" : "fałszywe"}.</p>
      <p><strong>Zdanie 2.</strong> Mnożymy potęgi, a „trzy razy większa" oznacza dodatkowy czynnik <InlineMath math="3=3^1" />:</p>
      <BlockMath math={`3\\cdot(3^{${a}}\\cdot 3^{${b}})=3^1\\cdot 3^{${a + b}}=3^{${real2}}`} />
      <p>Zdanie 2. jest {data.correctPF[1] === "P" ? "prawdziwe" : "fałszywe"}.</p>
      <p>Odpowiedź: <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\ 2.\\ \\text{${data.correctPF[1]}}`} />.</p>
    </div>
  );
}
